import { Box, Grid, MenuItem, Select, Typography } from "@mui/material";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect, useState } from "react";
import { Email } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import TemplateEditButton from "components/TemplateEditButton";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { useRegisterSaveAsNew } from "utils/useRegisterSaveAsNew";

import styles from "./styles";

interface SelectTemplateProps {
  scheduledEmail: Email;
  handleChangeTemplate: (templateId: number) => void;
}

export default function SelectTemplate({
  scheduledEmail,
  handleChangeTemplate,
}: SelectTemplateProps) {
  const { templates, outreachTemplates, followupTemplates } = useContext(
    OutreachTemplatesContext,
  );
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null,
  );
  const [userOpenEditDialog, setUserOpenEditDialog] = useState<boolean>(false);

  useRegisterSaveAsNew((newTemplate) => {
    setSelectedTemplate(newTemplate);
    if (newTemplate.id) {
      handleChangeTemplate(newTemplate.id);
    }
  });

  const getOptions = () => {
    if (scheduledEmail.contentType === TemplateContentType.OUTREACH) {
      return outreachTemplates;
    } else if (scheduledEmail.contentType === TemplateContentType.FOLLOWUP) {
      return followupTemplates;
    }
    return templates;
  };
  const options = getOptions();

  const changeTemplate = (e: CustomEvent) => {
    const { value } = e.target;
    const template = options?.find((t: Template) => t.id === value);
    if (!template || !template.id) {
      setSelectedTemplate(null);
      return;
    }
    handleChangeTemplate(template.id);
    setSelectedTemplate(template);
  };

  const renderTemplateOption = () => {
    return (
      <Select
        onChange={(e: CustomEvent) => changeTemplate(e)}
        value={selectedTemplate?.id || scheduledEmail?.templateId || ""}
        size="small"
        sx={[
          styles.templateSelect,
          styles.noSelectBorder,
          styles.templateWidth,
        ]}
        displayEmpty
      >
        <MenuItem value="">
          <em>Change Template</em>
        </MenuItem>
        {options?.map((template: Template, index: number) => (
          <MenuItem
            key={index}
            value={template?.id}
            sx={styles.templateMenuItem}
          >
            <Box sx={styles.templateMenuName}>{template?.name}</Box>
          </MenuItem>
        ))}
      </Select>
    );
  };

  const matchingTemplate = options?.find(
    (template: Template) => template.id === scheduledEmail?.templateId,
  );

  useEffect(() => {
    if (matchingTemplate && matchingTemplate?.id !== selectedTemplate?.id) {
      setSelectedTemplate(matchingTemplate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduledEmail?.templateId]);

  useEffect(() => {
    if (matchingTemplate?.id && userOpenEditDialog) {
      handleChangeTemplate(matchingTemplate?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userOpenEditDialog, matchingTemplate?.body]);

  return (
    <>
      <Grid container alignItems="center" sx={{ pl: 2, mt: 1 }} gap={1}>
        <Box>
          <Typography component="span" sx={styles.annotation}>
            Template:
          </Typography>
          {templates?.length > 0 && renderTemplateOption()}
        </Box>
        {scheduledEmail?.templateId && matchingTemplate?.id && (
          <Box onClick={() => setUserOpenEditDialog(true)}>
            <TemplateEditButton
              sx={{ ml: -2 }}
              templateId={matchingTemplate?.id}
            />
          </Box>
        )}
      </Grid>
    </>
  );
}
